import React, { useState } from 'react';
import { Edit2, ChevronDown, ChevronUp, Star, Trash2, AlertTriangle, Package } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { quantityStep, sanitizeQuantity, formatQuantity } from 'core/utils/units';
import CartItemDetails from './CartItemDetails';

const CartItem = ({
  item,
  index,
  isSelected,
  onToggleSelection,
  onEditItem,
  updateCartQuantity,
  formatPrice,
  calculateItemTotal,
  user,
  onAddToFavorites
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [imgError, setImgError] = useState(false);

  // Position number - always use index in cart view (1-based)
  const positionNumber = index !== undefined ? index + 1 : (item.position_number || 1);

  // Get theme colors based on Kontierungstyp
  const getThemeColors = () => {
    const kontierungstyp = item.kontierungstyp || 'kostenstelle';

    switch (kontierungstyp.toLowerCase()) {
      case 'kostenstelle':
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-green-900/10 to-gray-900/30 border border-green-400/20'
            : 'bg-white border border-slate-200',
          iconBg: isDarkMode ? 'from-green-600/60 via-green-700/50 to-green-800/60' : '',
          badgeColor: isDarkMode ? 'bg-green-600/30 border-green-400/40 text-green-300' : 'bg-green-100 border-green-300 text-green-700',
          label: t('cart.kontpiCostCenter')
        };
      case 'auftrag':
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-purple-900/10 to-gray-900/30 border border-purple-400/20'
            : 'bg-white border border-slate-200',
          iconBg: isDarkMode ? 'from-purple-600/60 via-purple-700/50 to-purple-800/60' : '',
          badgeColor: isDarkMode ? 'bg-purple-600/30 border-purple-400/40 text-purple-300' : 'bg-purple-100 border-purple-300 text-purple-700',
          label: t('cart.kontpiOrder')
        };
      case 'psp':
        // amber wie im BANF-Formular (BanfTypeSelector); Label = echtes PSP-Element,
        // nicht das frühere irreführende 'Projekt' auf dienstleistung
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-amber-900/10 to-gray-900/30 border border-amber-400/20'
            : 'bg-white border border-slate-200',
          iconBg: isDarkMode ? 'from-amber-600/60 via-amber-700/50 to-amber-800/60' : '',
          badgeColor: isDarkMode ? 'bg-amber-600/30 border-amber-400/40 text-amber-300' : 'bg-amber-100 border-amber-300 text-amber-700',
          label: t('banf.type.psp')
        };
      case 'material':
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-blue-900/10 to-gray-900/30 border border-blue-400/20'
            : 'bg-white border border-slate-200',
          iconBg: isDarkMode ? 'from-blue-600/60 via-blue-700/50 to-blue-800/60' : '',
          badgeColor: isDarkMode ? 'bg-blue-600/30 border-blue-400/40 text-blue-300' : 'bg-brand/10 border-brand/30 text-brand',
          label: t('banf.type.material')
        };
      default:
        return {
          cardBg: isDarkMode
            ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20'
            : 'bg-white border border-slate-200',
          iconBg: isDarkMode ? 'from-gray-600/60 via-gray-700/50 to-gray-800/60' : '',
          badgeColor: isDarkMode ? 'bg-gray-600/30 border-gray-400/40 text-gray-300' : 'bg-slate-100 border-slate-200 text-slate-600',
          label: t('cart.kontpiDefault')
        };
    }
  };

  const themeColors = getThemeColors();

  // Format delivery date
  const formatDeliveryDate = (date) => {
    if (!date) return null;
    try {
      const d = new Date(date);
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return date;
    }
  };

  // Check for missing required fields
  const getMissingFields = () => {
    const missing = [];
    if (!item.cost_center && !item.kostenstelle) missing.push(t('cart.missingCostCenter'));
    if (!item.gl_account && !item.sachkonto && item.kontierungstyp !== 'material' && item.kontierungstyp !== 'auftrag') missing.push(t('cart.missingSachkonto'));
    if (item.kontierungstyp === 'auftrag' && !item.auftrag) missing.push(t('cart.missingOrder'));
    if (!item.delivery_date) missing.push(t('cart.missingDeliveryDate'));
    if (!item.plant) missing.push(t('cart.missingPlant'));
    return missing;
  };

  const missingFields = getMissingFields();
  const hasMissingFields = missingFields.length > 0;

  // Simple inline field chip
  const renderField = (label, value, isMissing = false) => {
    if (!value && !isMissing) return null;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${
        isMissing
          ? isDarkMode
            ? 'bg-red-900/20 border-red-500/30 text-red-400'
            : 'bg-red-50 border-red-200 text-red-600'
          : isDarkMode
            ? 'bg-white/5 border-white/10 text-white/60'
            : 'bg-slate-50 border-slate-200 text-slate-500'
      }`}>
        <span className={`font-medium ${
          isMissing
            ? isDarkMode ? 'text-red-400' : 'text-red-600'
            : isDarkMode ? 'text-white/40' : 'text-slate-400'
        }`}>{label}</span>
        <span className={
          isMissing
            ? isDarkMode ? 'text-red-300' : 'text-red-700'
            : isDarkMode ? 'text-white/80' : 'text-slate-700'
        }>{value || '—'}</span>
      </span>
    );
  };

  // Bild-Slot: image_url → img mit Fallback-Icon
  const hasImage = item.image_url && !imgError;

  return (
    <div className="relative group/item">
      {/* Card Background */}
      <div className={`absolute inset-0 ${themeColors.cardBg} ${isDarkMode ? 'backdrop-blur-2xl' : ''} rounded-2xl shadow-sm group-hover/item:shadow-md transition-all duration-300`}></div>

      {/* Missing Fields Warning Indicator */}
      {hasMissingFields && (
        <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl ${isDarkMode ? 'bg-red-500/70' : 'bg-red-400'}`}></div>
      )}

      <div className="relative p-4">
        {/* Main Row: Checkbox + Position + Bild + Content + Price + Actions */}
        <div className="flex items-start gap-3">

          {/* Checkbox */}
          <div className="pt-1 flex-shrink-0">
            <input
              type="checkbox"
              checked={isSelected || false}
              onChange={() => onToggleSelection(item.id)}
              className={`w-4 h-4 rounded cursor-pointer transition-all ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-500 text-blue-500 focus:ring-blue-400'
                  : 'bg-white border-gray-300 text-blue-600 focus:ring-blue-500'
              } focus:ring-2`}
            />
          </div>

          {/* Position Badge */}
          <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border ${
            isDarkMode
              ? `bg-gradient-to-br ${themeColors.iconBg} border-white/20`
              : 'bg-brand/10 border-brand/20'
          }`}>
            <span className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-brand'}`}>{positionNumber}</span>
          </div>

          {/* Bild-Slot */}
          <div className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border flex items-center justify-center ${
            isDarkMode
              ? 'border-white/10 bg-white/5'
              : 'border-slate-200 bg-slate-50'
          }`}>
            {hasImage ? (
              <img
                src={item.image_url}
                alt={item.description || ''}
                onError={() => setImgError(true)}
                className="w-full h-full object-cover"
              />
            ) : (
              <Package className={`w-8 h-8 ${isDarkMode ? 'text-white/25' : 'text-slate-300'}`} />
            )}
          </div>

          {/* Content Area */}
          <div className="flex-1 min-w-0">
            {/* Title Row */}
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                {/* Artikelname */}
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'} truncate`} title={item.description}>
                  {item.description}
                </h3>
                {/* Material-/SAP-Nr. + Lieferant */}
                <div className={`flex flex-wrap gap-x-3 gap-y-0.5 text-xs mt-0.5 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>
                  {item.material && (
                    <span>{t('cart.prefixMat')} {item.material}</span>
                  )}
                  {(item.vendormat || item.vendor_material_number) && (
                    <span>{t('cart.prefixVendorNo')} {item.vendormat || item.vendor_material_number}</span>
                  )}
                  {(item.vendor || item.preferred_vendor) && (
                    <span>{item.vendor || item.preferred_vendor}</span>
                  )}
                </div>
                {/* Badges */}
                <div className="flex items-center gap-2 mt-1.5">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${themeColors.badgeColor}`}>
                    {themeColors.label}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                    isDarkMode ? 'bg-blue-600/20 border-blue-400/30 text-blue-300' : 'bg-brand/10 border-brand/30 text-brand'
                  }`}>
                    {item.belegart || 'NB'}
                  </span>
                  {hasMissingFields && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                      isDarkMode ? 'bg-red-600/20 border-red-400/30 text-red-300' : 'bg-red-100 border-red-300 text-red-700'
                    }`}>
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {t('cart.incomplete')}
                    </span>
                  )}
                </div>
              </div>

              {/* Price Display */}
              <div className="text-right flex-shrink-0">
                <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {calculateItemTotal(item.price, item.quantity)} {item.currency || 'EUR'}
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
                  {formatQuantity(item.quantity, item.unit)} {item.unit || 'ST'} &times; {formatPrice(item.price, '')}
                </div>
              </div>
            </div>

            {/* Kontierungs-Chips */}
            <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
              <div className="flex flex-wrap gap-1.5">
                {renderField(t('cart.fieldDeliveryDate'), formatDeliveryDate(item.delivery_date), !item.delivery_date)}
                {renderField(t('cart.fieldCostCenter'), item.cost_center || item.kostenstelle, !item.cost_center && !item.kostenstelle)}
                {item.kontierungstyp === 'auftrag'
                  ? renderField(t('cart.fieldOrder'), item.auftrag, !item.auftrag)
                  : renderField(t('cart.fieldGlAccount'), item.gl_account || item.sachkonto, !item.gl_account && !item.sachkonto && item.kontierungstyp !== 'material')
                }
                {renderField(t('cart.fieldPlant'), item.plant, !item.plant)}
                {item.anforderer && renderField(t('cart.fieldRequester'), item.anforderer)}
              </div>
              {/* Ändern-Link → onEditItem */}
              <button
                onClick={() => onEditItem(item)}
                className={`mt-1.5 text-xs font-medium transition-colors ${
                  isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-brand hover:text-[#1D4ED8]'
                }`}
              >
                {t('cart.editItem')}
              </button>
            </div>

            {/* Quantity Controls */}
            <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'} flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{t('cart.quantityLabel')}</span>
                <button
                  onClick={() => updateCartQuantity(item.id, sanitizeQuantity(Math.max(1, parseFloat(item.quantity) - 1), item.unit))}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg font-bold text-sm transition-colors ${
                    isDarkMode
                      ? 'bg-gray-800/50 hover:bg-red-600/30 text-white/70 hover:text-red-300 border border-white/10'
                      : 'bg-white hover:bg-red-50 text-gray-600 hover:text-red-600 border border-gray-200'
                  }`}
                >
                  &minus;
                </button>
                <input
                  type="number"
                  min="1"
                  step={quantityStep(item.unit)}
                  value={item.quantity}
                  onChange={(e) => updateCartQuantity(item.id, sanitizeQuantity(Math.max(1, parseFloat(e.target.value) || 1), item.unit))}
                  className={`w-14 text-center rounded-lg py-1.5 text-sm border focus:ring-2 focus:ring-blue-400/50 focus:outline-none ${
                    isDarkMode
                      ? 'bg-gray-800/30 text-white border-white/10'
                      : 'bg-white text-gray-800 border-gray-200'
                  } [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                />
                <button
                  onClick={() => updateCartQuantity(item.id, sanitizeQuantity(parseFloat(item.quantity) + 1, item.unit))}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg font-bold text-sm transition-colors ${
                    isDarkMode
                      ? 'bg-gray-800/50 hover:bg-green-600/30 text-white/70 hover:text-green-300 border border-white/10'
                      : 'bg-white hover:bg-green-50 text-gray-600 hover:text-green-600 border border-gray-200'
                  }`}
                >
                  +
                </button>
                <span className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{item.unit || 'ST'}</span>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    isDarkMode ? 'hover:bg-white/10 text-white/60 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
                  }`}
                  title={isExpanded ? t('cart.collapseTitle') : t('cart.detailsTitle')}
                >
                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => onAddToFavorites?.(item)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    isDarkMode ? 'hover:bg-yellow-500/20 text-white/40 hover:text-yellow-400' : 'hover:bg-yellow-50 text-gray-400 hover:text-yellow-600'
                  }`}
                  title={t('cart.addToFavorites')}
                >
                  <Star className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onEditItem(item)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    isDarkMode ? 'hover:bg-blue-500/20 text-white/40 hover:text-blue-400' : 'hover:bg-blue-50 text-gray-400 hover:text-blue-600'
                  }`}
                  title={t('cart.editItem')}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => updateCartQuantity(item.id, 0)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    isDarkMode ? 'hover:bg-red-500/20 text-white/40 hover:text-red-400' : 'hover:bg-red-50 text-gray-400 hover:text-red-600'
                  }`}
                  title={t('cart.removeItem')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Expandable Details */}
        <div className={`transition-all duration-300 overflow-hidden ${
          isExpanded ? 'max-h-[600px] opacity-100 mt-4' : 'max-h-0 opacity-0'
        }`}>
          <div className={`pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
            <CartItemDetails item={item} formatPrice={formatPrice} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
